import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Stack,
  Card,
  Button,
} from "@material-ui/core";
import { TextField, MenuItem } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import useAuth from "src/hooks/useAuth";
import axios from "src/utils/axios";
import { setUserSuccess } from "src/redux/slices/authJwt";
import { UploadAvatar } from "src/components/Upload";

// ----------------------------------------------------------------------
//   Step 0 · Datos de empresa. Nombre comercial, dirección fiscal,
//   NIF, teléfono, logo. Mismo endpoint que la ficha "Mi cuenta →
//   General" del portal (`PUT /authentication/update` + `POST /settings/logo`)
//   para que la config sea consistente y no haya campos duplicados.
//
//   El wizard lo pinta como PRIMER step del onboarding — el user
//   pasa de trial-registro a portal con los datos básicos ya rellenos
//   y no ve el ticket sin nombre del local hasta configurarlo después.
// ----------------------------------------------------------------------

// Prefijos telefónicos más frecuentes para el catálogo del wizard.
// Cubrimos ES + países limítrofes / LatAm más comunes. El user puede
// añadir uno manual introduciéndolo en el input (tipo "Otro" via el
// campo de texto libre — el Select mantiene el last-known valor).
const PHONE_PREFIXES = [
  { code: "+34", label: "🇪🇸 +34" },
  { code: "+351", label: "🇵🇹 +351" },
  { code: "+33", label: "🇫🇷 +33" },
  { code: "+39", label: "🇮🇹 +39" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+52", label: "🇲🇽 +52" },
  { code: "+54", label: "🇦🇷 +54" },
  { code: "+56", label: "🇨🇱 +56" },
  { code: "+57", label: "🇨🇴 +57" },
  { code: "+1", label: "🇺🇸 +1" },
];

// Separa un número guardado como "+34 600123456" en {prefix, rest}.
// Si no empieza por "+" asume prefijo España (+34) por defecto.
function splitPhone(raw) {
  const s = (raw || "").trim();
  if (!s) return { prefix: "+34", rest: "" };
  const m = s.match(/^(\+\d{1,4})[\s-]?(.*)$/);
  if (m) return { prefix: m[1], rest: m[2].trim() };
  return { prefix: "+34", rest: s };
}

// Junta prefix + rest en el formato canónico "+PP RESTO" que el backend
// espera (SmsUtil hace trim + strip whitespace al enviar). Devuelve
// string vacío si no hay número real.
function joinPhone(prefix, rest) {
  const p = (prefix || "").trim();
  const r = (rest || "").trim();
  if (!r) return "";
  return p ? `${p} ${r}` : r;
}

export default function StepEmpresa({ onComplete, onSkip }) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);

  const [nombreEmpresa, setNombreEmpresa] = useState(user?.nombreEmpresa || "");
  const [nif, setNif] = useState(user?.nif || "");
  const initialFiscal = splitPhone(user?.telefono);
  const [telefonoPrefix, setTelefonoPrefix] = useState(initialFiscal.prefix);
  const [telefono, setTelefono] = useState(initialFiscal.rest);
  // Teléfono de contacto principal (WhatsApp/SMS). Es el CANAL real por
  // donde envía notificaciones el backend (`User.NotificationPhones` —
  // ver `PaymentService`, `SmsUtil`). Distinto del `Telefono` fiscal de
  // arriba, aunque en la mayoría de casos coincide. Obligatorio.
  const initialNotifPhone = (user?.notificationPhones || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)[0]
    || user?.telefono || "";
  const initialNotif = splitPhone(initialNotifPhone);
  const [notificationPhonePrefix, setNotificationPhonePrefix] = useState(initialNotif.prefix);
  const [notificationPhone, setNotificationPhone] = useState(initialNotif.rest);
  // Cuando el user intenta guardar y hay campos obligatorios vacíos,
  // marcamos `attempted=true` para que los TextField afectados se
  // pinten en rojo con helper text de error — patrón "formulario
  // típico" que evita depender solo del snackbar.
  const [attempted, setAttempted] = useState(false);
  const [direccion, setDireccion] = useState(user?.direccion || "");
  const [codigoPostal, setCodigoPostal] = useState(user?.codigoPostal || "");
  const [localidad, setLocalidad] = useState(user?.localidad || "");
  const [provincia, setProvincia] = useState(user?.provincia || "");
  const [logo, setLogo] = useState({
    preview: user?.logoUrl || null,
    data: null,
    file: null,
  });

  const handleDropAvatar = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setLogo({
        preview: URL.createObjectURL(file),
        data: reader.result,
        file,
      });
    };
    reader.readAsDataURL(file);
  };

  const nombreInvalid = !nombreEmpresa.trim();
  const phoneInvalid = notificationPhone.trim().length < 6;

  const handleContinue = async () => {
    if (nombreInvalid || phoneInvalid) {
      setAttempted(true);
      enqueueSnackbar(
        "Rellena los campos marcados en rojo antes de continuar",
        { variant: "warning" },
      );
      return;
    }
    setSaving(true);
    try {
      // Logo (opcional). Solo lo mandamos si se cambió — igual patrón
      // que General.js: array `settings` con `key=CompanyLogo`.
      if (logo.data && logo.file) {
        await axios.post(
          "settings/logo",
          JSON.stringify({
            settings: [
              {
                name: logo.file.name,
                value: logo.data,
                key: "CompanyLogo",
              },
            ],
          }),
          { headers: { "Content-Type": "application/json" } },
        );
      }

      // Datos fiscales / de contacto — recomponemos con prefijo antes
      // de mandar al backend (formato canónico "+PP RESTO").
      const telefonoFull = joinPhone(telefonoPrefix, telefono);
      const notificationPhoneFull = joinPhone(notificationPhonePrefix, notificationPhone);
      const res = await axios.put("authentication/update", {
        FirstName: user?.firstName || nombreEmpresa,
        NombreEmpresa: nombreEmpresa.trim(),
        LastName: user?.lastName || "",
        Telefono: telefonoFull,
        // Fusionamos con los teléfonos de notificación ya guardados —
        // si el user ya había puesto varios en NotificationSettings,
        // preservamos el resto y sustituimos SOLO el primero (que es
        // el "principal" que edita este step).
        NotificationPhones: (() => {
          const rest = (user?.notificationPhones || "")
            .split(",").map((s) => s.trim()).filter(Boolean).slice(1);
          return [notificationPhoneFull, ...rest].filter(Boolean).join(",");
        })(),
        isDemo: user?.isDemo,
        finishDemoTrial: user?.finishDemoTrial,
        Pais: user?.pais || "ES",
        Provincia: provincia.trim(),
        Localidad: localidad.trim(),
        CodigoPostal: codigoPostal.trim(),
        Direccion: direccion.trim(),
        Nif: nif.trim(),
        Email: user?.email,
      });
      dispatch(setUserSuccess(res.data));
      enqueueSnackbar("Datos guardados", { variant: "success" });
      onComplete?.({
        dataJson: JSON.stringify({ nombreEmpresa, hasLogo: !!logo.data }),
      });
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.message || err?.message || "Error guardando datos",
        { variant: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
        Datos de tu local
      </Typography>
      <Typography sx={{ color: "text.secondary", mb: 3 }}>
        Esta información aparece en tus tickets y facturas. Puedes cambiarla
        después desde <strong>Mi cuenta → General</strong>.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, textAlign: "center" }}>
            <UploadAvatar
              accept="image/*"
              file={logo.preview}
              onDrop={handleDropAvatar}
              caption={
                <Typography variant="caption" sx={{ mt: 2, mx: "auto", display: "block", color: "text.secondary" }}>
                  Sube tu logo (JPG/PNG)
                </Typography>
              }
            />
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            <TextField
              label="Nombre del local *"
              fullWidth
              required
              value={nombreEmpresa}
              onChange={(e) => setNombreEmpresa(e.target.value)}
              error={attempted && nombreInvalid}
              helperText={
                attempted && nombreInvalid
                  ? "El nombre del local es obligatorio"
                  : "Aparece en el ticket. Ej: 'La Taberna del Centro'."
              }
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="NIF / CIF"
                fullWidth
                value={nif}
                onChange={(e) => setNif(e.target.value)}
              />
              <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  select
                  label="Prefijo"
                  value={telefonoPrefix}
                  onChange={(e) => setTelefonoPrefix(e.target.value)}
                  sx={{ minWidth: 110 }}
                >
                  {PHONE_PREFIXES.map((p) => (
                    <MenuItem key={p.code} value={p.code}>{p.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Teléfono fiscal"
                  fullWidth
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="600 12 34 56"
                  helperText="Aparece en tickets y facturas."
                />
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1}>
              <TextField
                select
                label="Prefijo"
                value={notificationPhonePrefix}
                onChange={(e) => setNotificationPhonePrefix(e.target.value)}
                sx={{ minWidth: 110 }}
              >
                {PHONE_PREFIXES.map((p) => (
                  <MenuItem key={p.code} value={p.code}>{p.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Teléfono de contacto (WhatsApp/SMS) *"
                fullWidth
                required
                value={notificationPhone}
                onChange={(e) => setNotificationPhone(e.target.value)}
                placeholder="600 12 34 56"
                error={attempted && phoneInvalid}
                helperText={
                  attempted && phoneInvalid
                    ? "El teléfono de contacto es obligatorio (te enviaremos avisos por WhatsApp/SMS)"
                    : "Aquí te llegarán los avisos importantes (fichajes, reservas, incidencias). Puedes añadir más números después."
                }
              />
            </Stack>
            <TextField
              label="Dirección"
              fullWidth
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Código postal"
                sx={{ width: { xs: "100%", sm: 160 } }}
                value={codigoPostal}
                onChange={(e) => setCodigoPostal(e.target.value)}
              />
              <TextField
                label="Localidad"
                fullWidth
                value={localidad}
                onChange={(e) => setLocalidad(e.target.value)}
              />
              <TextField
                label="Provincia"
                fullWidth
                value={provincia}
                onChange={(e) => setProvincia(e.target.value)}
              />
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
        <Button color="inherit" onClick={onSkip} disabled={saving}>
          Completar después
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          onClick={handleContinue}
          sx={{ minWidth: 180 }}
        >
          Guardar datos
        </LoadingButton>
      </Stack>
    </Box>
  );
}
